import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchDevEmailCode, seedDevKnock } from "../../lib/api";
import { resetAndStartOver, usePendingCodeEmail } from "../../lib/dev";
import { devIdentitiesQuery, meQuery, qk } from "../../lib/queries";

const STRIP_OPEN_KEY = "nob.dev.strip.open.v1";

function readOpen(): boolean {
  try {
    return window.localStorage.getItem(STRIP_OPEN_KEY) !== "0";
  } catch {
    return true;
  }
}

/**
 * THE FLOW-TESTING STRIP — a development affordance, and nothing else.
 *
 * It renders only when BOTH are true: the API reports its identity port on the
 * local stand-in, and the signed-in account is one of the DISPOSABLE stand-in
 * identities (`test_*`). One query answers both, and answers them the honest
 * way round: `/api/v1/dev/fake-x/identities` is empty against a real provider, and
 * `disposable` is the server's own reading of the handle rule rather than a
 * second copy of it here.
 *
 * That pairing is deliberate. @maya on a laptop is a play account with a
 * fortnight of history behind her, and a "reset this user" button floating over
 * every screen she is signed in to is a Friday afternoon waiting to happen. The
 * strip follows the identity that exists to be thrown away.
 *
 * Two verbs, matching what walking the eight flows actually needs
 * (`docs/testing-flows.md`): start this identity over, and fill the inbox.
 * Everything else a tester needs is the product.
 *
 * PLUS ONE FACT IT READS RATHER THAN DOES: the six-digit code the onboarding
 * Email step just posted (ruling R-0826-3). That step is REQUIRED and has no
 * skip, so without this line a tester would leave the browser for a terminal at
 * step 2 of every one of the eight flows. The address comes from the screen
 * that asked (`lib/dev`'s in-memory announcement) and the code from the mock
 * mail adapter's outbox, server side — the stored code is a salted digest and
 * stays one.
 *
 * "SAMPLE KNOCKS", PLURAL, AND ON PURPOSE (ruling R-0825-3). One knock cannot
 * exercise a console whose whole job is comparison, so a press seeds five
 * strangers of deliberately different quality and a second press seeds five
 * more. The count comes back from the server rather than being written here:
 * how many people a batch is is the server's decision, and a number repeated on
 * this side is a number that will one day be wrong.
 *
 * Bottom-left rather than bottom-right, dashed rather than solid, and monospace:
 * it must not read as product chrome for a beat, and it must not sit on top of
 * the composer or the desk, which live on the right.
 */
export function DevStrip() {
  const queryClient = useQueryClient();
  const { data: me } = useQuery(meQuery());
  const { data: identities = [] } = useQuery(devIdentitiesQuery());
  const [busy, setBusy] = useState<"reset" | "knock" | null>(null);
  const [note, setNote] = useState<string | null>(null);
  /**
   * Expanded unless this browser has said otherwise. Read once, on the first
   * render, because it is a preference and not a subscription — and written
   * straight through, because a reset RELOADS the page and a preference that
   * lived only in React state would come back open every single time.
   *
   * `localStorage` directly rather than `lib/draft`: that file's keys are all
   * about the PERSON and are swept on sign-out, and this one is about the
   * window. It must survive the sweep, so it must not be in the sweep.
   */
  const [open, setOpenState] = useState(() => readOpen());
  /**
   * The code for the address a screen is currently waiting on, polled while it
   * waits. A poll rather than a push because the mail leaves through a port
   * this browser cannot watch, and two seconds is well inside the ten minutes
   * the code lives.
   */
  const pendingEmail = usePendingCodeEmail();
  const [code, setCode] = useState<string | null>(null);
  useEffect(() => {
    if (!pendingEmail) {
      setCode(null);
      return;
    }
    let live = true;
    const read = () => {
      void fetchDevEmailCode(pendingEmail).then((c) => {
        if (live) setCode(c);
      });
    };
    read();
    const timer = setInterval(read, 2000);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [pendingEmail]);
  const setOpen = (next: boolean): void => {
    setOpenState(next);
    try {
      window.localStorage.setItem(STRIP_OPEN_KEY, next ? "1" : "0");
    } catch {
      /* private mode: the preference simply does not persist */
    }
  };

  const handle = me?.xHandle ?? null;
  const disposable =
    handle !== null && identities.some((id) => id.handle === handle && id.disposable);
  if (!disposable || handle === null) return null;

  async function sendSampleKnocks(): Promise<void> {
    setBusy("knock");
    setNote(null);
    const r = await seedDevKnock();
    if (r.ok) {
      setNote(`${r.seeded} knocks landed`);
      // The list is the thing the tester is about to look at, and the recap
      // reads the same knocks. Both are prefix-invalidated the way the desk's
      // own actions do it — one arrival changes every filter's count.
      await queryClient.invalidateQueries({ queryKey: ["inbox"] });
      await queryClient.invalidateQueries({ queryKey: qk.threads() });
      await queryClient.invalidateQueries({ queryKey: qk.recap() });
    } else {
      setNote(r.message);
    }
    setBusy(null);
  }

  return (
    <div
      className="fixed bottom-2 left-2 z-50 flex items-center gap-2 rounded border border-dashed
                 border-fainter bg-surface/95 px-2 py-1 font-mono text-[11px] text-muted shadow"
      // Named so the whole strip is findable in one query, and so a screenshot
      // review can say "the dev strip" and mean something exact.
      aria-label="Development flow-testing strip"
    >
      {/*
        THE COLLAPSE IS NOT DECORATION. The bottom-left corner is where the
        sidebar keeps its identity footer — the signed-in row and "Not you?" —
        and a floating bar parked on top of a real control is a dev tool that
        breaks the thing it exists to test. Collapsed, it is a five-character
        pill that covers nothing; the choice is remembered, so it survives the
        reload every reset performs.
      */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="text-fainter"
        title={open ? "Collapse the dev strip" : "Expand the dev strip"}
      >
        dev{open ? " ·" : ""}
      </button>
      {!open ? null : (
        <>
          <span className="text-fainter">@{handle}</span>
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => void sendSampleKnocks()}
            className="underline underline-offset-2 disabled:opacity-50"
          >
            {busy === "knock" ? "Knocking…" : "Send me sample knocks"}
          </button>
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => {
              setBusy("reset");
              setNote(null);
              void resetAndStartOver(handle).then((r) => {
                // A success never lands here: the page is already navigating away.
                if (!r.ok) setNote(r.message);
                setBusy(null);
              });
            }}
            className="underline underline-offset-2 disabled:opacity-50"
          >
            {busy === "reset" ? "Resetting…" : "Reset this user & start over"}
          </button>
          {/* The code, if a screen is waiting on one. Loud, because it is the
              only thing on this strip a tester needs to READ rather than press. */}
          {pendingEmail && (
            <span role="status" className="text-text">
              code: {code ?? "…"}
            </span>
          )}
          {note && (
            <span role="status" className="text-fainter">
              {note}
            </span>
          )}
        </>
      )}
    </div>
  );
}
